import {
	pgEnum,
	pgTable,
	pgSequence,
	boolean,
	integer,
	numeric,
	real,
	text,
	date,
	uuid,
	index,
	uniqueIndex
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import type { SQL } from "drizzle-orm";

import { baseTableProps } from "../../baseTableProps";
import { investments } from "../investments/investments.model";
import { displayNameProps } from "../_shared/display-name";

export const investmentListingStatusEnum = pgEnum("investment_listing_status", [
	"Draft",
	"Active",
	"Under Contract",
	"Closed",
	"Withdrawn",
	"Expired"
]);

export const investmentListingsSequenceNumberSeq = pgSequence(
	"investment_listings_sequence_number_seq",
	{
		startWith: 100000,
		increment: 1,
		cache: 1
	}
);

export const investmentListings = pgTable("investment_listings", {
	...baseTableProps,
	sequenceNumber: integer("sequence_number")
		.notNull()
		.default(sql`nextval('investment_listings_sequence_number_seq')`),
	...displayNameProps,
	slug: text("slug").notNull(),
	legacySlug: text("legacy_slug"),
	status: investmentListingStatusEnum("status").notNull().default("Draft"),
	closedDate: date("closed_date"),
	listPrice: real("list_price"),
	currentPrice: real("current_price"),
	priceChangeAmount: real("price_change_amount").generatedAlwaysAs(
		(): SQL => sql`"current_price" - "list_price"`
	),
	priceChangePercent: numeric("price_change_percent", {
		precision: 12,
		scale: 2,
		mode: "number"
	}).generatedAlwaysAs(
		(): SQL =>
			sql`round(("current_price"::numeric - "list_price"::numeric) / NULLIF("list_price"::numeric, 0) * 100, 2)`
	),
	listDate: date("list_date"),
	currentDaysOnMarket: integer("current_days_on_market"),
	description: text("description"),
	propertyType: text("property_type"),
	propertySubType: text("property_sub_type"),
	exteriorFeatures: text("exterior_features").array(),
	interiorFeatures: text("interior_features").array(),
	virtualTourUrl: text("virtual_tour_url"),
	publishedToMls: boolean("published_to_mls").notNull().default(false),
	marketingFacebook: boolean("marketing_facebook").notNull().default(false),
	marketingInstagram: boolean("marketing_instagram").notNull().default(false),
	marketingMarketplace: boolean("marketing_marketplace").notNull().default(false),
	listingEntryMethod: text("listing_entry_method"),
	mustSellBy: date("must_sell_by"),
	mlsId: text("mls_id"),
	parkingSpaces: integer("parking_spaces"),
	parkingType: text("parking_type"),
	addressStreet1: text("address_street_1"),
	addressStreet2: text("address_street_2"),
	addressCity: text("address_city"),
	addressState: text("address_state"),
	addressZip: text("address_zip"),
	latitude: numeric("latitude", { precision: 10, scale: 7, mode: "number" }),
	longitude: numeric("longitude", { precision: 10, scale: 7, mode: "number" }),
	beds: integer("beds"),
	baths: real("baths"),
	livingArea: integer("living_area"),
	lotSize: real("lot_size"),
	yearBuilt: integer("year_built")
});

export const xInvestmentListings = pgTable(
	"x_investment_listings",
	{
		...baseTableProps,
		listingId: uuid("listing_id")
			.references(() => investmentListings.id, {
				name: "x_investment_listings_listing_id_investment_listings_id_fk",
				onDelete: "cascade"
			})
			.notNull(),
		investmentId: uuid("investment_id")
			.references(() => investments.id, {
				name: "x_investment_listings_investment_id_investments_id_fk",
				onDelete: "cascade"
			})
			.notNull(),
		isPrimary: boolean("is_primary").notNull().default(false)
	},
	(table) => [
		uniqueIndex("x_investment_listings_one_primary_per_investment")
			.on(table.investmentId)
			.where(sql`${table.isPrimary}`),
		index("x_investment_listings_listing_id_idx").on(table.listingId),
		index("x_investment_listings_investment_id_idx").on(table.investmentId)
	]
);
